import { Component, ViewEncapsulation } from '@angular/core';
import { ResourceBundleService } from '@app/services/resource-bundle/resource-bundle.service';
import { get } from 'lodash-es';
import * as moment from 'moment';

@Component({
  selector: 'pixie-countdown-clock',
  templateUrl: './countdown-clock.component.html',
  styleUrls: ['./countdown-clock.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CountdownClockComponent {

  data;
  dateExpired = false;
  formatArray = [];
  title;
  position;
  dateData;
  ariaClock;
  clockStyles;
  styles;
  link;

  constructor(
    private resourceBundle: ResourceBundleService,
  ) {
      this.resourceBundle.get('countdownClock').then(result => {
          const config = get(result, 'countdownClock[0].generalPurposeStrings', false);
          const content = get(result, 'countdownClock[0].descriptions.countdownClock.sections', false);
          const links = get(result, 'countdownClock[0].descriptions.countdownClock.webLinks', false);

        if (!config || !content || !links) {
            return;
        }

        this.data = {...config, ...content, ...links};
        document.body.classList.add('countdown-clock-active'); // to remove bottom margin on hero
        this.formatArray = (this.data.format && this.data.format.split(':')) || [];
        this.title = this.data.title;
        this.link = this.data.link1;

        // By default the position is center middle
        // Additional positions supported for hero section:
        // center-top
        // center-bottom
        // Additional positions supported for image blocks section:
        // top-right
        // top-left
        // bottom-right
        // bottom-left
        this.position = this.data.position;
        this.styles = {
            color: this.data.fontColor,
            'background-color': this.data.backgroundColor
        };
        this.clockStyles = {
            'background-color': this.data.clockBackgroundColor,
            color: this.data.clockFontColor,
            'border-color': this.data.clockBorderColor
        };

        this.dateData = {
            ww: {
                label: this.data.weeks,
                value: 0
            },
            dd: {
                label: this.data.days,
                value: 0
            },
            hh: {
                label: this.data.hours,
                value: 0
            },
            mm: {
                label: this.data.minutes,
                value: 0
            },
            ss: {
                label: this.data.seconds,
                value: 0
            }
        };
        this.activate();
    }, (error) => {
        console.error('Failure loading countdown data:', error);
    });
  }

  activate(): void {
    const interval = 1000,
      targetDate = moment(this.data.targetDay),
      nowDate = moment();
    let refreshIntervalId;

    console.log('countdownClockController::now: ' + nowDate.format());
    // check if the target date is expired and don't show the countdown clock.
    if (this.isTargetDateExpired(nowDate, targetDate)) {
        console.error('countdownClockController::targetDate is expired: ' + targetDate, this.data.targetDay);
        this.setEndState();

        return;
    }

    // check if the targetDate is a valid date.
    if (!targetDate.isValid()) {
        console.error(`countdownClock::targetDate ${this.data.targetDay} is invalid at ${targetDate.invalidAt()}`);
        this.dateExpired = true;

        return;
    }

    // SetInverval replaced by $interval for testing purposes
    refreshIntervalId = setInterval(() => {
            nowDate.add(1, 'second');

            // if target date is expired clear the interval.
            if (this.isTargetDateExpired(nowDate, targetDate)) {
                this.setEndState();

                return clearInterval(refreshIntervalId);
            }

            this.getClock(targetDate, nowDate);
        }, interval);

      // Set the data for the ARIA clock
    this.getAriaClock(targetDate, nowDate);

  }

  setEndState() {
    this.dateExpired = true;
    this.formatArray = [];
    this.title = this.data.expiredText;
    this.link = this.data.link2;
  }


  isTargetDateExpired(nowDate, targetDay): boolean {
    return nowDate.isSame(targetDay) || nowDate.isAfter(targetDay);
  }

  validateDigits(value: number): string {
      const stringValue = value.toString();
      if (stringValue.length === 1) {
          return '0' + value;
      }

      return stringValue;
  }

  getClock(targetDate, nowDate): void {
    const nowDateClone = nowDate.clone();

    // To get the countdown clock values we need first get the total of weeks and
    // then decrease those weeks in the difference to calculate remaining days and then the same
    // to calculate the remaining hours and so on.
    // We tried to use moment.duration but was only working if we show weeks:days:hours:minutes:seconds
    // all at the same time, for example duration.days() will return a number from 1 to 31 only,
    // and our requeriments says we need to support different formats like
    // hours:minutes or days:hours:minutes etc.
    if (this.formatArray.indexOf('ww') !== -1) {
        this.dateData.ww.value = targetDate.diff(nowDateClone, 'weeks');
        this.dateData.ww.values = this.validateDigits(this.dateData.ww.value).split('');
    }
    if (this.formatArray.indexOf('dd') !== -1) {
        this.dateData.dd.value = targetDate.diff(
            nowDateClone.add(this.dateData.ww.value, 'weeks'), 'days'
        );
        this.dateData.dd.values = this.validateDigits(this.dateData.dd.value).split('');
    }
    if (this.formatArray.indexOf('hh') !== -1) {
        this.dateData.hh.value = targetDate.diff(
            nowDateClone.add(this.dateData.dd.value, 'days'), 'hours'
        );
        this.dateData.hh.values = this.validateDigits(this.dateData.hh.value).split('');
    }
    if (this.formatArray.indexOf('mm') !== -1) {
        this.dateData.mm.value = targetDate.diff(
            nowDateClone.add(this.dateData.hh.value, 'hours'), 'minutes'
        );
        this.dateData.mm.values = this.validateDigits(this.dateData.mm.value).split('');
    }
    if (this.formatArray.indexOf('ss') !== -1) {
        this.dateData.ss.value = targetDate.diff(
            nowDateClone.add(this.dateData.mm.value, 'minutes'), 'seconds'
        );
        this.dateData.ss.values = this.validateDigits(this.dateData.ss.value).split('');
    }
  }

  getAriaClock(targetDate, nowDate): void {
    // Get the clock values
    this.getClock(targetDate, nowDate);
    this.ariaClock = this.formatArray.map((format) => {
        return this.dateData[format].value + ' ' + this.dateData[format].label;
    });

    // We don't care about seconds for the ARIA clock, so pop 'em if we got 'em
    if (this.formatArray.indexOf('ss') !== -1) {
        this.ariaClock.pop();
    }

    // Join the array into a nice string for the screen reader
    this.ariaClock = this.ariaClock.join(', ');
  }
}
